/** Traffic-light evaluation for the device Overview tab. */

import { evaluateHardwarePosture } from '@/lib/hardwareTrafficLight'
import { evaluateSecurityPosture } from '@/lib/securityTrafficLight'
import {
  assessmentChecks,
  normaliseCheckStatus,
  normaliseToolkitStatus,
  primaryFinding,
} from '@/lib/toolkitHealth'
import {
  mapSectionResults,
  normaliseIssue,
  sortIssuesBySeverity,
  buildPosture,
} from '@/lib/trafficLight'

export { TRAFFIC } from '@/lib/trafficLight'

/** Hardware glance tiles open the Hardware tab. */
export const OVERVIEW_HARDWARE_SECTION_IDS = new Set([
  'memory',
  'processor',
  'storage',
  'firmware',
])

const HEADLINES = {
  green: 'Device looks healthy overall',
  amber: 'Review recommended — some areas need attention',
  red: 'Action needed — one or more areas require attention',
  grey: 'Limited data — run RMM scripts and sync for a full picture',
}

/** Toolkit checks already owned by Hardware tiles on Overview. */
const HARDWARE_THEME_PATTERNS = {
  memory: [/memory/i, /\bram\b/i],
  storage: [/disk/i, /free space/i, /storage/i, /\bdrive\b/i],
}

/** Pull issues from nested posture, keeping each item's real severity. */
function issuesFromPosture(posture, limit = 3) {
  const issues = []
  for (const section of posture?.sections ?? []) {
    if (section.status === 'grey' || section.status === 'green') continue
    for (const issue of section.issues) {
      const normalised = normaliseIssue(issue, section.status)
      if (!normalised) continue
      issues.push(normalised)
    }
  }
  if (
    issues.length === 0 &&
    posture?.overall?.status &&
    !['green', 'grey'].includes(posture.overall.status)
  ) {
    issues.push({
      text: posture.overall.headline,
      status: posture.overall.status,
    })
  }
  return sortIssuesBySeverity(issues).slice(0, limit)
}

/** Surface each non-OK hardware area as its own Overview tile (matches Hardware tab). */
function hardwareOverviewTiles(hardwarePosture) {
  return (hardwarePosture?.sections ?? [])
    .filter((section) => section.status === 'red' || section.status === 'amber')
    .map((section) => ({
      id: section.id,
      title: section.title,
      status: section.status,
      issues: (section.issues ?? [])
        .map((issue) => normaliseIssue(issue, section.status))
        .filter(Boolean)
        .slice(0, 3),
    }))
}

function coveredHardwareThemes(hardwarePosture) {
  return new Set(
    (hardwarePosture?.sections ?? [])
      .filter((section) => section.status === 'red' || section.status === 'amber')
      .map((section) => section.id)
      .filter((id) => HARDWARE_THEME_PATTERNS[id]),
  )
}

function textCoveredByHardware(text, themes) {
  if (!themes.size) return false
  const value = String(text || '')
  for (const theme of themes) {
    const patterns = HARDWARE_THEME_PATTERNS[theme] || []
    if (patterns.some((pattern) => pattern.test(value))) return true
  }
  return false
}

/**
 * Overview Toolkit tile: hide memory/disk findings already shown on Hardware tiles
 * so the glance strip does not double-flag the same capacity problem.
 */
function evaluateToolkit({ toolkitSummary, hardwarePosture }) {
  const status = normaliseToolkitStatus(toolkitSummary?.overall_status)
  const hasData = Boolean(status || toolkitSummary?.checks_run != null)

  if (!hasData) {
    return { id: 'toolkit', status: 'grey', issues: [] }
  }

  const themes = coveredHardwareThemes(hardwarePosture)
  const checks = assessmentChecks(toolkitSummary)
  const remainingChecks = checks.filter((check) => {
    const label = check.check || check.name || check.area || ''
    const detail = check.detail || check.message || ''
    return !textCoveredByHardware(`${label} ${detail}`, themes)
  })

  let traffic = 'green'
  const issues = []

  if (checks.length > 0 && themes.size > 0) {
    const hasFail = remainingChecks.some(
      (check) => normaliseCheckStatus(check.status) === 'FAIL',
    )
    const hasWarn = remainingChecks.some(
      (check) => normaliseCheckStatus(check.status) === 'WARN',
    )
    if (hasFail) {
      traffic = 'red'
    } else if (hasWarn) {
      traffic = 'amber'
    } else {
      traffic = 'green'
    }

    const failing = remainingChecks.filter((check) => {
      const checkStatus = normaliseCheckStatus(check.status)
      return checkStatus === 'FAIL' || checkStatus === 'WARN'
    })
    for (const check of failing.slice(0, 3)) {
      const title = check.check || check.name || 'Check'
      const detail = check.detail || check.message
      issues.push(detail ? `${title} — ${detail}` : title)
    }
  } else {
    if (status === 'FAIL' || (toolkitSummary?.failures ?? 0) > 0) {
      traffic = 'red'
    } else if (status === 'WARN' || (toolkitSummary?.warnings ?? 0) > 0) {
      traffic = 'amber'
    }

    const finding = primaryFinding(toolkitSummary)
    const findingText = finding
      ? finding.detail
        ? `${finding.title} — ${finding.detail}`
        : finding.title
      : ''
    if (findingText && finding.title !== 'No issues flagged') {
      if (!textCoveredByHardware(findingText, themes)) {
        issues.push(findingText)
      } else if (traffic !== 'green') {
        // Sole finding already on Hardware — clear Toolkit tile severity.
        traffic = 'green'
      }
    }

    if (traffic !== 'green' && issues.length === 0) {
      if (toolkitSummary?.failures > 0) {
        issues.push(`${toolkitSummary.failures} check failure(s)`)
      } else if (toolkitSummary?.warnings > 0) {
        issues.push(`${toolkitSummary.warnings} warning(s)`)
      } else if (traffic === 'red') {
        issues.push('Health check failed')
      } else {
        issues.push('Review recommended')
      }
      // If those generic counts were only capacity-related we cannot tell without
      // checks; leave severity as reported by Toolkit overall.
    }
  }

  return {
    id: 'toolkit',
    status: traffic,
    issues: [...new Set(issues)].slice(0, 3),
  }
}

function evaluateConnectivity({ agent }) {
  const issues = []
  let status = 'grey'

  if (agent?.online_status === true) {
    status = 'green'
  } else if (agent?.online_status === false) {
    status = 'amber'
    issues.push({ text: 'Device is offline', status: 'amber' })
  }

  return { id: 'connectivity', status, issues }
}

export function evaluateOverviewPosture({
  agent,
  hardware = [],
  rmmData = null,
  toolkitSummary = null,
  thresholds = null,
}) {
  const snapshots = rmmData?.snapshots ?? []
  const events = rmmData?.events ?? []

  const securityPosture = evaluateSecurityPosture({
    snapshots,
    events,
    threatdown: rmmData?.threatdown ?? null,
    avProduct: agent?.av_product ?? null,
    avStatus: agent?.av_status ?? null,
    exclusions: agent?.posture_exclusions,
  })
  const hardwarePosture = evaluateHardwarePosture({
    agent,
    hardware,
    snapshots,
    thresholds,
    exclusions: agent?.posture_exclusions,
  })
  const hardwareTiles = hardwareOverviewTiles(hardwarePosture)

  const sectionDefs = [
    { id: 'security', title: 'Security' },
    ...hardwareTiles.map((tile) => ({ id: tile.id, title: tile.title })),
    { id: 'toolkit', title: 'Toolkit health' },
    { id: 'connectivity', title: 'Connectivity' },
  ]

  const evaluators = [
    {
      id: 'security',
      status: securityPosture.overall.status,
      issues: issuesFromPosture(securityPosture, 3),
    },
    ...hardwareTiles.map((tile) => ({
      id: tile.id,
      status: tile.status,
      issues: tile.issues,
    })),
    evaluateToolkit({ toolkitSummary, hardwarePosture }),
    evaluateConnectivity({ agent }),
  ]

  const sections = mapSectionResults(sectionDefs, evaluators)
  return buildPosture({ sections, sectionDefs, headlines: HEADLINES })
}
